import { AlertType } from '@types';
import { AlertModal, Card, PageHeader } from '_atoms';
import { AdminService } from '_services';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdClose,
  MdEdit,
  MdExpandLess,
  MdExpandMore,
  MdSave,
  MdSportsHockey,
} from 'react-icons/md';
import { HockeyLesson } from '../../@types/HockeyLesson';

const HockeyLessonsPage: React.FC = () => {
  const [hockeyLessons, setHockeyLessons] = useState<HockeyLesson[]>([]);
  const [expandedLessons, setExpandedLessons] = useState<Set<string>>(
    new Set(),
  );
  const [editingLessons, setEditingLessons] = useState<Set<string>>(new Set());
  const [isLoadingSave, setIsLoadingSave] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handleLessonChange = (
    lessonId: string,
    field: keyof HockeyLesson,
    value: string | number | boolean,
  ) => {
    setHockeyLessons(prev =>
      prev.map(lesson =>
        lesson.id === lessonId ? { ...lesson, [field]: value } : lesson,
      ),
    );
  };

  const handleEditToggle = (lessonId: string) => {
    setEditingLessons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId);
      } else {
        newSet.add(lessonId);
        // Always expand when entering edit mode
        setExpandedLessons(prevExpanded => {
          const newExpandedSet = new Set(prevExpanded);
          newExpandedSet.add(lessonId);
          return newExpandedSet;
        });
      }
      return newSet;
    });
  };

  const handleExpandToggle = (lessonId: string) => {
    setExpandedLessons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(lessonId)) {
        newSet.delete(lessonId);
      } else {
        newSet.add(lessonId);
      }
      return newSet;
    });
  };

  const handleSaveLesson = useCallback(
    async (lesson: HockeyLesson) => {
      setIsLoadingSave(true);

      try {
        await AdminService.updateHockeyLesson(lesson);
        showAlert('success', 'Success', 'Hockey lesson updated successfully!');
        setEditingLessons(prev => {
          const newSet = new Set(prev);
          newSet.delete(lesson.id);
          return newSet;
        });
      } catch (error) {
        console.error('Error updating hockey lesson:', error);
        showAlert(
          'error',
          'Error',
          'Error updating hockey lesson. Please try again.',
        );
      } finally {
        setIsLoadingSave(false);
      }
    },
    [showAlert],
  );

  // Load hockey lessons on page load
  useEffect(() => {
    const loadHockeyLessons = async () => {
      setIsLoadingInitial(true);
      try {
        const responseData =
          (await AdminService.getHockeyLessons()) as unknown as HockeyLesson[];

        setHockeyLessons(responseData);
      } catch (error) {
        console.error('Error loading hockey lessons:', error);
        showAlert('error', 'Error', 'Failed to load hockey lessons.');
      } finally {
        setIsLoadingInitial(false);
      }
    };

    loadHockeyLessons();
  }, [showAlert]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={MdSportsHockey}
        title="Hockey Lessons Management"
        description="Configure hockey lessons, pricing, and availability settings."
      />

      {/* Main Content */}
      {isLoadingInitial ? (
        <Card>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading hockey lessons...</p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {hockeyLessons.length === 0 ? (
            <Card>
              <div className="text-center py-8">
                <p className="text-gray-600">No hockey lessons found.</p>
              </div>
            </Card>
          ) : (
            hockeyLessons.map(lesson => {
              const isExpanded = expandedLessons.has(lesson.id);
              const isEditing = editingLessons.has(lesson.id);

              return (
                <Card key={lesson.id} className="flex flex-col">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => handleExpandToggle(lesson.id)}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                      >
                        {isExpanded ? (
                          <MdExpandLess className="w-5 h-5 text-gray-600" />
                        ) : (
                          <MdExpandMore className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {lesson.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {lesson.description}
                        </p>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-xs text-gray-500">
                            Price: ${lesson.price}
                          </span>
                          <span className="text-xs text-gray-500">
                            Capacity: {lesson.slotsAvailable}/
                            {lesson.maxCapacity}
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full ${
                              lesson.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {lesson.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEditToggle(lesson.id)}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                      >
                        {isEditing ? (
                          <MdClose className="mr-1" />
                        ) : (
                          <MdEdit className="mr-1" />
                        )}
                        {isEditing ? 'Cancel' : 'Edit'}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 space-y-4">
                      {isEditing ? (
                        // Edit view
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Title
                            </label>
                            <input
                              type="text"
                              value={lesson.title}
                              onChange={e =>
                                handleLessonChange(
                                  lesson.id,
                                  'title',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <textarea
                              value={lesson.description}
                              onChange={e =>
                                handleLessonChange(
                                  lesson.id,
                                  'description',
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[100px] resize-y"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Price ($)
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={lesson.price}
                                onChange={e =>
                                  handleLessonChange(
                                    lesson.id,
                                    'price',
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Max Capacity
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={lesson.maxCapacity}
                                onChange={e =>
                                  handleLessonChange(
                                    lesson.id,
                                    'maxCapacity',
                                    parseInt(e.target.value) || 1,
                                  )
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                              </label>
                              <div className="flex items-center">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleLessonChange(
                                      lesson.id,
                                      'isActive',
                                      !lesson.isActive,
                                    )
                                  }
                                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                    lesson.isActive
                                      ? 'bg-blue-600'
                                      : 'bg-gray-200'
                                  }`}
                                >
                                  <span
                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                      lesson.isActive
                                        ? 'translate-x-6'
                                        : 'translate-x-1'
                                    }`}
                                  />
                                </button>
                                <span className="ml-3 text-sm text-gray-700">
                                  {lesson.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Save Button */}
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => handleSaveLesson(lesson)}
                              disabled={
                                isLoadingSave ||
                                !lesson.title.trim() ||
                                !lesson.description.trim() ||
                                lesson.price <= 0 ||
                                lesson.maxCapacity <= 0
                              }
                              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                              <MdSave className="mr-2" />
                              {isLoadingSave ? 'Saving...' : 'Save Lesson'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Read-only view
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description
                            </label>
                            <p className="text-sm text-gray-600">
                              {lesson.description}
                            </p>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Price
                              </label>
                              <p className="text-sm text-gray-600">
                                ${lesson.price}
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Max Capacity
                              </label>
                              <p className="text-sm text-gray-600">
                                {lesson.maxCapacity}
                              </p>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Available Slots
                              </label>
                              <p className="text-sm text-gray-600">
                                {lesson.slotsAvailable}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};

export default HockeyLessonsPage;
